import { useCallback, useEffect, useState } from 'react'
import { logger } from '@/utils/logging'

type AirPlayState = 'unavailable' | 'available' | 'connected'

interface UseAirPlayOptions {
  videoRef: React.RefObject<HTMLVideoElement | null>
}

interface UseAirPlayReturn {
  airPlayState: AirPlayState
  showAirPlayPicker: () => void
}

/**
 * Hook that detects AirPlay availability and manages AirPlay sessions.
 *
 * Uses the standard Remote Playback API where supported, with a fallback
 * to Safari's `webkitShowPlaybackTargetPicker` for older WebKit browsers.
 *
 * AirPlay is only available on Apple devices (macOS Safari, iOS Safari).
 * The hook returns `unavailable` on unsupported browsers.
 */
export function useAirPlay({ videoRef }: UseAirPlayOptions): UseAirPlayReturn {
  const [airPlayState, setAirPlayState] = useState<AirPlayState>('unavailable')

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Standard Remote Playback API (Chrome also supports this for Cast, but
    // we only use it for AirPlay since Google Cast has its own SDK)
    if ('remote' in video && video.remote) {
      const remote = video.remote as RemotePlayback

      remote
        .watchAvailability((available) => {
          setAirPlayState((prev) => {
            if (available && prev === 'unavailable') return 'available'
            if (!available && prev !== 'connected') return 'unavailable'
            return prev
          })
        })
        .catch(() => {
          // watchAvailability not supported — check for Safari fallback
          if ('webkitShowPlaybackTargetPicker' in video) {
            setAirPlayState('available')
          }
        })

      const handleConnecting = () => setAirPlayState('available')
      const handleConnect = () => setAirPlayState('connected')
      const handleDisconnect = () => setAirPlayState('available')

      remote.addEventListener('connecting', handleConnecting)
      remote.addEventListener('connect', handleConnect)
      remote.addEventListener('disconnect', handleDisconnect)

      return () => {
        remote.cancelWatchAvailability().catch(() => {
          // Ignore — cleanup best-effort
        })
        remote.removeEventListener('connecting', handleConnecting)
        remote.removeEventListener('connect', handleConnect)
        remote.removeEventListener('disconnect', handleDisconnect)
      }
    }

    // Safari fallback — webkitShowPlaybackTargetPicker exists but no Remote Playback API
    if ('webkitShowPlaybackTargetPicker' in video) {
      setAirPlayState('available')

      const handleCurrentPlaybackChange = () => {
        const webkitVideo = video as HTMLVideoElement & {
          webkitCurrentPlaybackTargetIsWireless?: boolean
        }
        setAirPlayState(webkitVideo.webkitCurrentPlaybackTargetIsWireless ? 'connected' : 'available')
      }

      video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', handleCurrentPlaybackChange)
      return () => {
        video.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', handleCurrentPlaybackChange)
      }
    }

    // No AirPlay support — state stays 'unavailable'
  }, [videoRef])

  const showAirPlayPicker = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    try {
      // Standard Remote Playback API
      if ('remote' in video && video.remote) {
        const remote = video.remote as RemotePlayback
        remote.prompt().catch((error) => {
          // User cancelled or no devices available — not an error
          logger().info('AirPlay prompt cancelled or failed', { error })
        })
        return
      }

      // Safari fallback
      const webkitVideo = video as HTMLVideoElement & {
        webkitShowPlaybackTargetPicker?: () => void
      }
      if (webkitVideo.webkitShowPlaybackTargetPicker) {
        webkitVideo.webkitShowPlaybackTargetPicker()
      }
    } catch (error) {
      logger().warn('Failed to show AirPlay picker', {}, error)
    }
  }, [videoRef])

  return { airPlayState, showAirPlayPicker }
}
